/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Hold the assistant overrides a teacher sets mid-lesson, per run.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; live policy model; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens; localStorage; API client
 * @public-api live module
 * @side-effects in-memory UI updates only
 * @tests modules/live/model/agent-policy.test.ts
 */

import { create } from 'zustand';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';
import type { PolicyOverride } from '../model/agent-policy';

interface AgentPolicyState {
  overrides: Readonly<Record<string, readonly PolicyOverride[]>>;
  add(runId: string, override: PolicyOverride): void;
  revoke(runId: string, id: string): void;
  clear(): void;
}

export const useAgentPolicy = create<AgentPolicyState>((set) => ({
  overrides: {},
  add: (runId, override) =>
    set((state) => ({
      overrides: { ...state.overrides, [runId]: [...(state.overrides[runId] ?? []), override] },
    })),
  revoke: (runId, id) =>
    set((state) => ({
      overrides: {
        ...state.overrides,
        [runId]: (state.overrides[runId] ?? []).filter((item) => item.id !== id),
      },
    })),
  clear: () => set({ overrides: {} }),
}));

export function selectOverrides(
  overrides: Readonly<Record<string, readonly PolicyOverride[]>>,
  runId: string,
): readonly PolicyOverride[] {
  return overrides[runId] ?? [];
}

registerWorkspaceTransientReset('agent-policy', () => useAgentPolicy.getState().clear());
